import { useCallback, useRef, useState } from 'react'
import { GripHorizontal, ImageIcon, Pencil, Trash2 } from 'lucide-react'
import type { CoverImageMeta } from './useCoverImage'
import { cn } from '@/lib/utils'

interface CoverImageProps {
  displayUrl: string | null
  meta: CoverImageMeta | null
  coverImageType: string | null | undefined
  hasCover: boolean
  onChangeCover: () => void
  onRemoveCover: () => void
  onPositionChange: (positionY: number) => void
}

export function CoverImage({
  displayUrl,
  meta,
  coverImageType,
  hasCover,
  onChangeCover,
  onRemoveCover,
  onPositionChange,
}: CoverImageProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [isRepositioning, setIsRepositioning] = useState(false)
  const [dragPositionY, setDragPositionY] = useState<number | null>(null)
  const latestDragPositionRef = useRef<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dragStartRef = useRef<{ startY: number; startPosition: number } | null>(null)

  const positionY = dragPositionY ?? meta?.positionY ?? 50

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isRepositioning) return
      e.preventDefault()
      dragStartRef.current = {
        startY: e.clientY,
        startPosition: positionY,
      }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!dragStartRef.current || !containerRef.current) return
        const containerHeight = containerRef.current.offsetHeight
        const deltaY = moveEvent.clientY - dragStartRef.current.startY
        const deltaPercent = (deltaY / containerHeight) * 100
        const newPosition = Math.max(
          0,
          Math.min(100, dragStartRef.current.startPosition - deltaPercent),
        )
        latestDragPositionRef.current = newPosition
        setDragPositionY(newPosition)
      }

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)

        if (dragStartRef.current) {
          const finalPosition = latestDragPositionRef.current ?? positionY
          setIsRepositioning(false)
          setDragPositionY(null)
          latestDragPositionRef.current = null
          dragStartRef.current = null
          onPositionChange(finalPosition)
        }
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [isRepositioning, positionY, onPositionChange],
  )

  if (!hasCover || !displayUrl) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[200px] overflow-hidden group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        if (isRepositioning) {
          setIsRepositioning(false)
          setDragPositionY(null)
        }
      }}
      onMouseDown={handleMouseDown}
    >
      <img
        src={displayUrl}
        alt="Cover"
        className={cn(
          'w-full h-full object-cover',
          isRepositioning && 'cursor-grab active:cursor-grabbing',
        )}
        style={{ objectPosition: `center ${positionY}%` }}
        draggable={false}
      />

      {/* Unsplash attribution */}
      {coverImageType === 'unsplash' && meta?.unsplashAttribution && (
        <div className="absolute bottom-2 left-2 text-xs text-white/70 drop-shadow-md">
          Photo by{' '}
          <a
            href={`${meta.unsplashAttribution.authorUrl}?utm_source=moly&utm_medium=referral`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-white"
          >
            {meta.unsplashAttribution.authorName}
          </a>{' '}
          on{' '}
          <a
            href="https://unsplash.com/?utm_source=moly&utm_medium=referral"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-white"
          >
            Unsplash
          </a>
        </div>
      )}

      {/* Hover overlay with actions */}
      {isHovered && !isRepositioning && (
        <div className="absolute top-2 right-2 flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setIsRepositioning(true)
            }}
            className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-black/50 hover:bg-black/70 rounded-md transition"
          >
            <GripHorizontal className="size-3" />
            Reposition
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onChangeCover()
            }}
            className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-black/50 hover:bg-black/70 rounded-md transition"
          >
            <Pencil className="size-3" />
            Change
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onRemoveCover()
            }}
            className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-black/50 hover:bg-black/70 rounded-md transition"
          >
            <Trash2 className="size-3" />
            Remove
          </button>
        </div>
      )}

      {/* Reposition instruction */}
      {isRepositioning && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <span className="text-sm text-white bg-black/60 px-3 py-1.5 rounded-md">
            Drag to reposition
          </span>
        </div>
      )}
    </div>
  )
}

/** Subtle hover button shown when no cover is set */
export function AddCoverButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="group/cover relative h-0">
      <button
        type="button"
        onClick={onClick}
        className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 text-xs text-muted-foreground bg-secondary/80 hover:bg-secondary rounded-md opacity-0 group-hover/cover:opacity-100 transition-opacity z-10"
      >
        <ImageIcon className="size-3.5" />
        Add cover
      </button>
    </div>
  )
}
